import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Search, SearchResult } from '@app/core/models';
import { ModalService } from '@app/core/services';
import { ToastsService } from '@app/core/services/toasts.service';
import { MessageModalComponent } from '@app/shared/message-modal.component';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, catchError, distinctUntilChanged, map, switchMap } from 'rxjs';
import { ResultsService } from '../../services/results.service';
import {
  deleteSearch, deleteSearchResult, deleteSearchResultSuccess, deleteSearchSuccess,
  loadFTPSavedSearches, loadFTPSavedSearchesSuccess, loadFTPSearchLookupData, loadFTPSearchLookupDataSuccess, loadGlobalLookupData, loadGlobalLookupDataSuccess,
  loadRestrictedAnalytesLookup, loadRestrictedAnalytesLookupSuccess,
  loadSavedSearches, loadSavedSearchesSuccess, loadSearchLookupData, loadSearchLookupDataSuccess, loadSearchResults, loadSearchResultsSuccess,
  onScheduleSavedSearch,
  onScheduleSavedSearchFailure,
  onScheduleSavedSearchSuccess,
  saveSearch,
  saveSearchFailure,
  saveSearchSuccess
} from '../actions/results.actions';
import { AppState } from '../reducers';



@Injectable()
export class ResultsEffects {

  constructor(protected actions: Actions, private store: Store<AppState>, private resultsService: ResultsService,
    protected router: Router, private toastService: ToastsService, private modalService: ModalService) { }

  loadSavedSearches = createEffect(() => {
    return this.actions.pipe(
      ofType(loadSavedSearches),
      switchMap(() => this.resultsService.getSavedSearches()),
      map((iSavedSearches) => {
        let savedSearches = [];
        iSavedSearches.forEach(i => savedSearches.push(new Search(i)));
        return loadSavedSearchesSuccess({ payload: savedSearches });
      })
    )
  });

  loadFTPSavedSearches = createEffect(() => {
    return this.actions.pipe(
      ofType(loadFTPSavedSearches),
      switchMap(() => this.resultsService.getFTPSavedSearches()),
      map((iSavedSearches) => {
        let savedSearches = [];
        iSavedSearches.forEach(i => savedSearches.push(new Search(i)));
        return loadFTPSavedSearchesSuccess({ payload: savedSearches });
      })
    )
  });

  loadSearchResults = createEffect(() => {
    return this.actions.pipe(
      ofType(loadSearchResults),
      distinctUntilChanged(),
      switchMap(() => this.resultsService.getSearchResults()),
      map((iSearchResults) => {
        let searchResults = [];
        if (iSearchResults && iSearchResults.length > 0) {
          iSearchResults.forEach(i => searchResults.push(new SearchResult(i)));
        }
        return loadSearchResultsSuccess({ payload: searchResults });
      })
    )
  });

  loadSearchLookupData = createEffect(() => {
    return this.actions.pipe(
      ofType(loadSearchLookupData),
      switchMap(() => this.resultsService.getSearchLookupData()),
      map((searchLookupData) => {
        return loadSearchLookupDataSuccess({ payload: searchLookupData });
      })
    )
  });

  loadGlobalLookupData = createEffect(() => {
    return this.actions.pipe(
      ofType(loadGlobalLookupData),
      switchMap(() => this.resultsService.getGlobalLookupData()),
      map((searchLookupData) => {
        return loadGlobalLookupDataSuccess({ payload: searchLookupData });
      })
    )
  });

  loadFTPSearchLookupData = createEffect(() => {
    return this.actions.pipe(
      ofType(loadFTPSearchLookupData),
      distinctUntilChanged(),
      switchMap((action) => this.resultsService.getFTPSearchLookupData(action.orgId)),
      map((searchLookupData) => {
        return loadFTPSearchLookupDataSuccess({ payload: searchLookupData });
      })
    )
  });


  loadRestrictedAnalytesLookup = createEffect(() => {
    return this.actions.pipe(
      ofType(loadRestrictedAnalytesLookup),
      distinctUntilChanged(),
      switchMap(() => this.resultsService.getRestrictedAnalytesLookup()),
      map((restrictedAnalytesLookup) => loadRestrictedAnalytesLookupSuccess({ payload: restrictedAnalytesLookup }))
    )
  });

  deleteSearch = createEffect(() => {
    return this.actions.pipe(
      ofType(deleteSearch),
      switchMap((action => {
        return this.resultsService.deleteSearch(action.search?.queryId).then(() => {

          if (action.search.query.ftpOrgId) {
            this.store.dispatch(loadFTPSavedSearches());
            this.router.navigate(['/portal/ftp']);
          } else {
            this.store.dispatch(loadSavedSearches());
            this.router.navigate(['/portal/results/saved-searches']);
          }

          const toastMessage = ` <strong>${action.search.queryName}</strong> was deleted.`
          this.toastService.success(toastMessage);

          return deleteSearchSuccess();
        });
      })
      ))
  });

  deleteSearchResult = createEffect(() => {
    return this.actions.pipe(
      ofType(deleteSearchResult),
      switchMap((action) => {
        return this.resultsService.deleteSearchResult(action.searchResult.resultId).pipe(
          map(() => {
            return deleteSearchResultSuccess({ searchResult: action.searchResult });
          })
        );
      })
    )
  });

  deleteSearchResultSuccess = createEffect(() => this.actions.pipe(
    ofType(deleteSearchResultSuccess),
    map(action => {
      this.store.dispatch(loadSearchResults());
      this.router.navigate(['/portal/results/search-results']);
      const toastMessage = ` <strong>${action.searchResult.resultName}</strong> was deleted.`
      this.toastService.success(toastMessage);
    })
  ), { dispatch: false });


  saveSearch = createEffect(() => {
    return this.actions.pipe(
      ofType(saveSearch),
      switchMap((action) => this.resultsService.saveSearch(action.search).then(search => {
        if (!action.isFtpSearch) {
          this.store.dispatch(loadSavedSearches());

          this.router.navigate(['/portal/results/search/' + search?.queryId]);
          if (!action.isSearchOptionSave) this.toastService.success('Your search was saved successfully.');
        } else {
          this.store.dispatch(loadFTPSavedSearches());

          this.router.navigate(['/portal/ftp/ftp-search/' + search?.queryId]);
          if (!action.isSearchOptionSave) this.toastService.success(`${action.search.queryName} saved successfully.`);
        }
        return saveSearchSuccess({ search: search, isFtpSearch: action.isFtpSearch })
      })),
      catchError(err => {
        this.toastService.warn('Your search could not be saved at this time.');
        return of(saveSearchFailure({ err: err }));
      }))
  });

  onScheduleSavedSearch = createEffect(() => {
    return this.actions.pipe(
      ofType(onScheduleSavedSearch),
      distinctUntilChanged(),
      switchMap((action) => this.resultsService.saveSearch(action.search)),
      map((iSearch) => {
        this.store.dispatch(loadSavedSearches());
        const toastMessage = ' Your scheduled search was saved successfully.'
        this.toastService.success(toastMessage);

        return onScheduleSavedSearchSuccess({ search: new Search(iSearch) });
      }),
      catchError(err => {
        const modalRef = this.modalService.open(MessageModalComponent);
        modalRef.componentInstance.modalText = ' Your scheduled search could not be saved at this time.';
        modalRef.componentInstance.title = 'Recurring Search Schedule Error';
        modalRef.componentInstance.labelConfirm = 'Close';
        modalRef.componentInstance.extraCssLinkBtn = 'invisible';
        modalRef.componentInstance.extraCssPrimaryBtn = 'btn-primary';


        return of(onScheduleSavedSearchFailure({ err: err }));
      }))
  });

}


